import React, { useState, useRef, useEffect } from 'react';
import MediaComponent from '../Media/media-component';
import SystemDataService from '../../Services/SystemDataService';

const UserWidget = ({ user, isAuthenticated, loginIcon, links, onDark = false, onNavigate, onLogout }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);

    const truckIcon = SystemDataService.byLabel(links, 'Truck Icon');
    const accountIcon = SystemDataService.byLabel(links, 'Login Icon');

    useEffect(() => {
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    // Colours flip with the navbar background: white text on the transparent
    // (over-hero) bar, dark text on the solid white bar so it stays visible.
    const borderCls = onDark ? 'border-white/10' : 'border-primary-200';
    const fgCls = onDark ? 'text-zinc-300 group-hover:text-white' : 'text-primary-500 group-hover:text-secondary-500';

    // ── Guest ──────────────────────────────────────────────────────────────────
    if (!isAuthenticated) {
        return (
            <button
                onClick={() => onNavigate('/login')}
                className={`hidden sm:flex items-center gap-2 border hover:border-secondary-500 rounded-full px-4 py-2 transition-all group ${borderCls}`}
            >
                {loginIcon?.image
                    ? <img src={loginIcon.image} alt="" className="h-3.5 w-3.5 object-contain opacity-40 group-hover:opacity-100 transition-opacity" />
                    : <svg className={`h-3.5 w-3.5 transition-colors ${fgCls}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                }
                <span className={`text-[10px] font-bold uppercase tracking-widest transition-colors ${fgCls}`}>
                    Sign In
                </span>
            </button>
        );
    }

    // ── Authenticated ──────────────────────────────────────────────────────────
    const isAdmin = user?.role === 'admin';
    const dashboardPath = isAdmin ? '/admin' : '/account';
    const firstName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || '';
    const initials = (user?.name || user?.email || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

    return (
        <div className="relative hidden sm:block" ref={ref}>
            {/* Trigger */}
            <button
                onClick={() => setIsOpen(o => !o)}
                className={`flex items-center gap-2.5 rounded-full px-3 py-1.5 border-2 transition-all duration-200
                    ${isOpen
                        ? 'bg-primary-900 border-white/20 shadow-lg'
                        : `bg-transparent hover:border-secondary-500 ${onDark ? 'border-white/10' : 'border-primary-200'}`
                    }`}
            >
                {/* Role badge, only on desktop */}
                {isAdmin && (
                    <span className={`hidden md:block text-[8px] font-bold uppercase tracking-[0.25em] px-2 py-0.5 rounded-full transition-colors
                        ${isOpen ? 'bg-secondary-500 text-white' : 'bg-red-500/20 text-red-400'}`}>
                        Admin
                    </span>
                )}

                {/* Initials circle */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold transition-colors
                    ${isOpen
                        ? 'bg-secondary-500 text-white'
                        : isAdmin ? 'bg-secondary-500/20 text-secondary-400' : (onDark ? 'bg-white/10 text-white' : 'bg-primary-100 text-primary-600')
                    }`}

                >
                    {initials}
                </div>

                {/* Name */}
                <span className={`text-[10px] font-bold uppercase tracking-widest hidden md:block max-w-[80px] truncate transition-colors
                    ${isOpen ? 'text-white' : (onDark ? 'text-white' : 'text-primary-600')}`}>
                    {firstName}
                </span>

                {/* Chevron */}
                <svg
                    className={`w-3 h-3 transition-all duration-200 ${isOpen ? 'text-white rotate-180' : (onDark ? 'text-zinc-400' : 'text-primary-400')}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown */}
            <div className={`absolute right-0 top-full mt-3 w-60 rounded-[1.75rem] overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.18)] z-[200]
                border border-white/10 bg-primary-950
                transition-all duration-200 origin-top-right
                ${isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'}`}
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-white/10">
                    <p className="text-[8px] font-bold uppercase  text-white/30 mb-1">
                        {isAdmin ? 'Admin Session' : ''}
                    </p>
                    {user?.name && (
                        <p className="text-sm font-secondary font-extralight text-white uppercase " >
                            {user.name}
                        </p>
                    )}
                    <p className="text-[10px] text-white/30 font-bold truncate mt-0.5">{user?.email}</p>
                </div>

                {/* Actions */}
                <div className="p-2 flex flex-col gap-0.5">
                    <button
                        onClick={() => { onNavigate(dashboardPath); setIsOpen(false); }}
                        className="flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-white/10 transition-colors group text-left"
                    >
                        <div className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-secondary-500/20 transition-colors shrink-0">
                            {accountIcon?.image
                                ? <img src={accountIcon.image} alt="" className="w-4 h-4 object-contain brightness-0 invert opacity-50 group-hover:opacity-100" />
                                : <svg className="w-3.5 h-3.5 text-white/50 group-hover:text-secondary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                            }
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-white/60 group-hover:text-white transition-colors">
                            {isAdmin ? 'Admin Panel' : 'My Orders'}
                        </span>
                    </button>


                    <div className="h-px bg-white/10 my-1 mx-2" />

                    <button
                        onClick={() => { onLogout(); setIsOpen(false); }}
                        className="flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-red-500/10 transition-colors group text-left"
                    >
                        <div className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-red-500/20 transition-colors shrink-0">
                            <svg className="w-3.5 h-3.5 text-white/50 group-hover:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 group-hover:text-red-400 transition-colors">
                            Sign Out
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserWidget;
